import { scaleTime } from 'd3-scale';
import { useId, useMemo, useRef, type PointerEvent } from 'react';
import { nearestIndex, pollWeightedAverage, type ChartPoint } from '../../../lib/metrics';
import { axisTimeFormatter, formatDateTime } from '../../../lib/time';
import { Card } from '../../atoms';
import { useChartSync } from './chartSync';
import { useElementWidth } from './useElementWidth';

export interface ReachabilityRow {
  key: string;
  label: string;
  /** Share of polls answered per bucket, 0–100. */
  points: ChartPoint[];
}

interface ReachabilityStripProps {
  rows: ReachabilityRow[];
  fromMs: number;
  toMs: number;
  binMs: number;
}

const STRIP_HEIGHT = 18;

/** Up/down reads better as a timeline than as a line at 100%: one cell per bucket, gaps where nothing was polled. */
export function ReachabilityStrip({ rows, fromMs, toMs, binMs }: ReachabilityStripProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(stripRef);
  const chartId = useId();
  const sync = useChartSync();
  const x = useMemo(() => scaleTime().domain([fromMs, toMs]).range([0, Math.max(1, width)]), [fromMs, toMs, width]);
  const cell = Math.max(1, x(fromMs + binMs) - x(fromMs));
  const gap = cell > 4 ? 1 : 0;
  const tickLabel = axisTimeFormatter(toMs - fromMs);
  const ticks = x.ticks(Math.max(2, Math.floor(width / 110))).filter((t, i, all) => i === 0 || tickLabel(t) !== tickLabel(all[i - 1]!));
  const hovering = sync.t !== null && sync.t >= fromMs && sync.t <= toMs;

  function onPointerMove(event: PointerEvent) {
    const rect = stripRef.current?.getBoundingClientRect();
    if (rect) sync.set(x.invert(event.clientX - rect.left).getTime(), chartId);
  }

  return (
    <Card as="section" className="col-span-full px-5 pb-4 pt-4" aria-labelledby={`${chartId}-title`}>
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 id={`${chartId}-title`} className="text-base">
            Reachability
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">Share of polls that got a response · grey where nothing was polled</p>
        </div>
        <div className="flex-none font-mono text-[11px] text-fg-muted">{hovering ? formatDateTime(sync.t!) : 'whole range'}</div>
      </header>

      <div className="grid grid-cols-[minmax(0,84px)_minmax(0,1fr)_56px] sm:grid-cols-[minmax(0,140px)_minmax(0,1fr)_64px] items-center gap-x-3 gap-y-2">
        {rows.map((row, i) => {
          const hoverIndex = hovering ? nearestIndex(row.points, sync.t!) : null;
          const value = hoverIndex !== null ? row.points[hoverIndex]?.avg : pollWeightedAverage(row.points);
          return (
            <div key={row.key} className="contents">
              <div className="truncate text-[13px] text-fg-secondary">{row.label}</div>
              <div
                ref={i === 0 ? stripRef : undefined}
                className="relative cursor-crosshair"
                style={{ height: STRIP_HEIGHT }}
                onPointerMove={onPointerMove}
                onPointerLeave={() => sync.set(null, null)}
                role="img"
                aria-label={`${row.label}: ${value === undefined ? 'no data' : `${value.toFixed(2)}% of polls answered`} in this range`}
              >
                <svg width="100%" height={STRIP_HEIGHT} className="block" aria-hidden>
                  <rect width="100%" height={STRIP_HEIGHT} rx={2} className="fill-grid" />
                  {width > 0 &&
                    row.points.map((p) => (
                      <rect
                        key={p.t}
                        x={x(p.t)}
                        width={Math.max(1, cell - gap)}
                        height={STRIP_HEIGHT}
                        className={p.avg >= 99.95 ? 'fill-good opacity-55' : p.avg >= 50 ? 'fill-warning' : 'fill-critical'}
                      />
                    ))}
                  {hovering && width > 0 && <line x1={x(sync.t!)} x2={x(sync.t!)} y1={0} y2={STRIP_HEIGHT} className="stroke-fg stroke-[1.5]" />}
                </svg>
              </div>
              <div className="text-right font-mono text-[13px] font-medium">{value === undefined ? '—' : `${value.toFixed(value >= 99.995 ? 0 : 2)}%`}</div>
            </div>
          );
        })}

        <div />
        <div className="relative h-4 font-mono text-[10.5px] text-fg-muted" aria-hidden>
          {width > 0 &&
            ticks.map((t) => (
              <span key={t.getTime()} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: x(t) }}>
                {tickLabel(t)}
              </span>
            ))}
        </div>
        <div />
      </div>
    </Card>
  );
}
